import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { connectionService, CreateConnectionRequest } from '../../services/connectionService';

interface ConnectionState {
  connections: Connection[];
  loading: boolean;
  error: string | null;
}

export interface Connection {
  id: string;
  platform: string;
  name: string;
  status: string;
  lastTested: string;
}

const initialState: ConnectionState = {
  connections: [],
  loading: false,
  error: null,
};

export const fetchConnections = createAsyncThunk(
  'connection/fetchAll',
  async (tenantId: string, { rejectWithValue }) => {
    try {
      return await connectionService.getAllConnections(tenantId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch connections');
    }
  }
);

export const createConnection = createAsyncThunk(
  'connection/create',
  async (data: CreateConnectionRequest, { rejectWithValue }) => {
    try {
      return await connectionService.createConnection(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create connection');
    }
  }
);

export const updateConnection = createAsyncThunk(
  'connection/update',
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      return await connectionService.updateConnection(id, data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update connection');
    }
  }
);

export const deleteConnection = createAsyncThunk(
  'connection/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await connectionService.deleteConnection(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete connection');
    }
  }
);

export const testConnection = createAsyncThunk(
  'connection/test',
  async (id: string, { rejectWithValue }) => {
    try {
      return await connectionService.testConnection(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Connection test failed');
    }
  }
);

const connectionSlice = createSlice({
  name: 'connection',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchConnections.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchConnections.fulfilled, (state, action) => {
        state.loading = false;
        state.connections = action.payload;
      })
      .addCase(fetchConnections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createConnection.fulfilled, (state, action) => {
        state.connections.push(action.payload);
      })
      .addCase(updateConnection.fulfilled, (state, action) => {
        const index = state.connections.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.connections[index] = action.payload;
        }
      })
      .addCase(deleteConnection.fulfilled, (state, action) => {
        state.connections = state.connections.filter(c => c.id !== action.payload);
      })
      .addCase(testConnection.fulfilled, (state, action) => {
        const index = state.connections.findIndex(c => c.id === action.meta.arg);
        if (index !== -1) {
          state.connections[index].status = action.payload.success ? 'connected' : 'error';
          state.connections[index].lastTested = new Date().toISOString();
        }
      });
  },
});

export default connectionSlice.reducer;
